import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Search20Regular, Mail16Regular, Copy16Regular } from '@fluentui/react-icons';
import { toast } from 'react-toastify';
import { GetTentMembers, SendTentEmail } from '../../redux/tentSlice';
import useBrowserTimezoneReport from '../../hooks/useBrowserTimezoneReport';
import { getEffectiveLocation, searchMember } from '../../utils/tentTimezoneUtils';
import EmailComposer from '../CommonComponents/EmailComposer/EmailComposer';
import MemberDetailPanel from './MemberDetailPanel';
import TentRow from './TentRow';
import styles from './css/index.module.css';

const GROUP_NONE = 'none';
const GROUP_ORG = 'organisation';
const GROUP_WORKSTREAM = 'workstream';

const uniqueSorted = values => Array.from(new Set(values.filter(Boolean))).sort((a, b) => a.localeCompare(b));

const Tent = () => {
    const dispatch = useDispatch();
    const { members, projectName, memberCount, inboundEmail, loading, emailSending } = useSelector(state => state.tent);
    const { selectedProject } = useSelector(state => state.projectData);
    const defaultEmailSubject = projectName || selectedProject?.project_name || 'Project';

    useBrowserTimezoneReport(true);

    const [search, setSearch] = React.useState('');
    const [orgFilter, setOrgFilter] = React.useState('all');
    const [workstreamFilter, setWorkstreamFilter] = React.useState('all');
    const [roleFilter, setRoleFilter] = React.useState('all');
    const [locationFilter, setLocationFilter] = React.useState('all');
    const [groupBy, setGroupBy] = React.useState(GROUP_NONE);
    const [selectedIds, setSelectedIds] = React.useState([]);
    const [activeMember, setActiveMember] = React.useState(null);
    const [composerOpen, setComposerOpen] = React.useState(false);
    const [composerRecipients, setComposerRecipients] = React.useState([]);
    const [composerSubject, setComposerSubject] = React.useState('');
    const [, setClockTick] = React.useState(0);

    React.useEffect(() => {
        dispatch(GetTentMembers());
    }, [dispatch, selectedProject?.id]);

    React.useEffect(() => {
        const timer = window.setInterval(() => setClockTick(t => t + 1), 60000);
        return () => window.clearInterval(timer);
    }, []);

    const filteredMembers = React.useMemo(() => {
        return (members || []).filter(member => {
            if (!searchMember(member, search)) return false;
            if (orgFilter !== 'all' && member.organisation !== orgFilter) return false;
            if (roleFilter !== 'all' && member.job_title !== roleFilter) return false;
            if (workstreamFilter !== 'all' && !(member.workstreams || []).includes(workstreamFilter)) return false;
            if (locationFilter !== 'all' && getEffectiveLocation(member) !== locationFilter) return false;
            return true;
        });
    }, [members, search, orgFilter, roleFilter, workstreamFilter, locationFilter]);

    const orgOptions = uniqueSorted((members || []).map(m => m.organisation));
    const roleOptions = uniqueSorted((members || []).map(m => m.job_title));
    const workstreamOptions = uniqueSorted((members || []).flatMap(m => m.workstreams || []));
    const locationOptions = uniqueSorted((members || []).map(m => getEffectiveLocation(m)).filter(Boolean));

    const toggleSelect = userId => {
        setSelectedIds(prev => (prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]));
    };

    const toggleSelectAll = () => {
        const visibleIds = filteredMembers.map(m => m.user_id);
        const allSelected = visibleIds.length > 0 && visibleIds.every(id => selectedIds.includes(id));
        if (allSelected) {
            setSelectedIds(prev => prev.filter(id => !visibleIds.includes(id)));
        } else {
            setSelectedIds(prev => Array.from(new Set([...prev, ...visibleIds])));
        }
    };

    const openComposer = (recipients, subject = '') => {
        setComposerRecipients(recipients);
        setComposerSubject(subject);
        setComposerOpen(true);
    };

    const handleEmailSelected = () => {
        const recipients = (members || []).filter(m => selectedIds.includes(m.user_id));
        if (!recipients.length) return;
        openComposer(recipients, defaultEmailSubject);
    };

    const handleCopyInbound = async () => {
        if (!inboundEmail) return;
        try {
            await navigator.clipboard.writeText(inboundEmail);
            toast.success('Project email copied');
        } catch {
            toast.error('Failed to copy project email');
        }
    };

    const handleCopyEmails = async () => {
        const emails = (members || [])
            .filter(m => selectedIds.includes(m.user_id))
            .map(m => m.email)
            .filter(Boolean);
        if (!emails.length) return;
        try {
            await navigator.clipboard.writeText(emails.join('; '));
            toast.success('Email addresses copied');
        } catch {
            toast.error('Failed to copy email addresses');
        }
    };

    const handleSendEmail = async ({ to, cc, subject, messageHtml }) => {
        try {
            const result = await dispatch(
                SendTentEmail({
                    toUserIds: to.map(r => r.user_id),
                    ccUserIds: cc.map(r => r.user_id),
                    subject,
                    messageHtml,
                }),
            ).unwrap();
            toast.success(
                result?.recipient_count > 1
                    ? `Email sent to ${result.recipient_count} recipients`
                    : `Email sent to ${to[0]?.full_name || 'recipient'}`,
            );
            setComposerOpen(false);
        } catch (error) {
            toast.error(typeof error === 'string' ? error : 'Email could not be sent. Please try again.');
        }
    };

    const renderRows = () => {
        if (groupBy === GROUP_ORG) {
            const groups = {};
            filteredMembers.forEach(member => {
                const key = member.organisation || 'Unknown organisation';
                groups[key] = groups[key] || [];
                groups[key].push(member);
            });
            return Object.entries(groups)
                .sort(([a], [b]) => a.localeCompare(b))
                .flatMap(([groupName, groupMembers]) => [
                    <tr key={`group-${groupName}`} className={styles.groupHeaderRow}>
                        <td colSpan={8}>▼ {groupName} ({groupMembers.length} members)</td>
                    </tr>,
                    ...groupMembers.map(member => (
                        <TentRow
                            key={member.user_id}
                            member={member}
                            selected={selectedIds.includes(member.user_id)}
                            onToggleSelect={toggleSelect}
                            onOpenMember={setActiveMember}
                            onEmail={() => openComposer([member], defaultEmailSubject)}
                        />
                    )),
                ]);
        }

        if (groupBy === GROUP_WORKSTREAM) {
            const rows = [];
            const wsNames = uniqueSorted(filteredMembers.flatMap(m => m.workstreams || []));
            wsNames.forEach(wsName => {
                const groupMembers = filteredMembers.filter(m => (m.workstreams || []).includes(wsName));
                if (!groupMembers.length) return;
                rows.push(
                    <tr key={`group-${wsName}`} className={styles.groupHeaderRow}>
                        <td colSpan={8}>▼ {wsName} ({groupMembers.length} members)</td>
                    </tr>,
                );
                groupMembers.forEach(member => {
                    rows.push(
                        <TentRow
                            key={`${wsName}-${member.user_id}`}
                            member={member}
                            selected={selectedIds.includes(member.user_id)}
                            onToggleSelect={toggleSelect}
                            onOpenMember={setActiveMember}
                            onEmail={() => openComposer([member], defaultEmailSubject)}
                        />
                    );
                });
            });
            return rows;
        }

        return filteredMembers.map(member => (
            <TentRow
                key={member.user_id}
                member={member}
                selected={selectedIds.includes(member.user_id)}
                onToggleSelect={toggleSelect}
                onOpenMember={setActiveMember}
                onEmail={recipients => openComposer(recipients, defaultEmailSubject)}
            />
        ));
    };

    const displayProjectName = projectName || selectedProject?.project_name || 'Project';
    const displayCount = memberCount || members.length;

    return (
        <div className={styles.tentPage}>
            <div className={styles.headerRow}>
                <div className={styles.titleBlock}>
                    <h1>The Tent</h1>
                    <div className={styles.subtitleRow}>
                        <span>People participating in this project</span>
                        <span className={styles.projectBadge}>
                            <span className={styles.projectDot} />
                            {displayProjectName} · {displayCount} members
                        </span>
                    </div>
                </div>
                {selectedIds.length > 0 && (
                    <div className={styles.bulkBar}>
                        <span className={styles.bulkCount}>{selectedIds.length} selected</span>
                        <button type="button" className={styles.bulkButtonPrimary} onClick={handleEmailSelected}>
                            <Mail16Regular />
                            Email selected
                        </button>
                        <button type="button" className={styles.bulkButtonSecondary} onClick={handleCopyEmails}>
                            <Copy16Regular />
                            Copy emails
                        </button>
                    </div>
                )}
            </div>

            {inboundEmail ? (
                <div className={styles.inboundBar}>
                    <div>
                        <div className={styles.inboundLabel}>Forward email into this project</div>
                        <div className={styles.inboundHint}>
                            Only a member of {displayProjectName} can file mail sent to this address.
                        </div>
                    </div>
                    <div className={styles.inboundAddressRow}>
                        <code className={styles.inboundAddress}>{inboundEmail}</code>
                        <button type="button" className={styles.inboundCopy} onClick={handleCopyInbound}>
                            <Copy16Regular />
                            Copy
                        </button>
                    </div>
                </div>
            ) : null}

            <div className={styles.toolbarRow}>
                <div className={styles.toolbarLeft}>
                    <div className={styles.searchBox}>
                        <Search20Regular />
                        <input
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="Search by name, organisation or role"
                        />
                    </div>
                    <select className={styles.filterSelect} value={orgFilter} onChange={e => setOrgFilter(e.target.value)}>
                        <option value="all">Organisation: All</option>
                        {orgOptions.map(option => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </select>
                    <select
                        className={styles.filterSelect}
                        value={workstreamFilter}
                        onChange={e => setWorkstreamFilter(e.target.value)}
                    >
                        <option value="all">Workstream: All</option>
                        {workstreamOptions.map(option => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </select>
                    <select className={styles.filterSelect} value={roleFilter} onChange={e => setRoleFilter(e.target.value)}>
                        <option value="all">Role: All</option>
                        {roleOptions.map(option => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </select>
                    <select
                        className={styles.filterSelect}
                        value={locationFilter}
                        onChange={e => setLocationFilter(e.target.value)}
                    >
                        <option value="all">Location: All</option>
                        {locationOptions.map(option => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </select>
                </div>
                <div className={styles.toolbarRight}>
                    <div className={styles.groupByWrap}>
                        <span>Group by</span>
                        <div className={styles.groupByButtons}>
                            {[GROUP_NONE, GROUP_ORG, GROUP_WORKSTREAM].map(option => (
                                <button
                                    key={option}
                                    type="button"
                                    className={`${styles.groupByButton} ${groupBy === option ? styles.groupByButtonActive : ''}`}
                                    onClick={() => setGroupBy(option)}
                                >
                                    {option === GROUP_NONE ? 'None' : option === GROUP_ORG ? 'Organisation' : 'Workstream'}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            <div className={styles.tableWrap}>
                {loading ? (
                    <div className={styles.loadingState}>Loading members...</div>
                ) : filteredMembers.length === 0 ? (
                    <div className={styles.emptyState}>No members match your filters.</div>
                ) : (
                    <table className={styles.table}>
                        <colgroup>
                            <col />
                            <col />
                            <col />
                            <col />
                            <col />
                            <col />
                            <col />
                            <col />
                        </colgroup>
                        <thead>
                            <tr>
                                <th className={styles.checkboxCell}>
                                    <input
                                        type="checkbox"
                                        aria-label="Select all visible members"
                                        onChange={toggleSelectAll}
                                        checked={
                                            filteredMembers.length > 0 &&
                                            filteredMembers.every(m => selectedIds.includes(m.user_id))
                                        }
                                    />
                                </th>
                                <th className={styles.nameColumnHeader}>Name</th>
                                <th>Organisation</th>
                                <th>Role</th>
                                <th>Workstreams</th>
                                <th>Location</th>
                                <th>Local Time</th>
                                <th className={styles.contactColumnHeader}>Contact</th>
                            </tr>
                        </thead>
                        <tbody>{renderRows()}</tbody>
                    </table>
                )}
            </div>

            {activeMember && (
                <MemberDetailPanel
                    member={activeMember}
                    onClose={() => setActiveMember(null)}
                    onEmail={recipients => openComposer(recipients, defaultEmailSubject)}
                />
            )}

            <EmailComposer
                open={composerOpen}
                onClose={() => setComposerOpen(false)}
                initialRecipients={composerRecipients}
                initialSubject={composerSubject}
                allMembers={members}
                sending={emailSending}
                onSend={handleSendEmail}
            />
        </div>
    );
};

export default Tent;
